import { FC, useEffect, useState } from 'react';

// API
import apibridge from '../../../apibridge';

// REDUX
import { useAppDispatch } from '../../../store/slice';
import { ToastMessagesSlice } from '../../../store/slice/ToastMessages';
import { AlertSlice } from '../../../store/slice/Alert';

// PACKAGES
import { Offcanvas, Spinner } from 'react-bootstrap';

// COMPONENTS
import DelayedFadeIn from '../../../components/_Helpers/DelayedFadeIn';
import RichText from '../../../components/RichText/RichText';
import SvgMask from '../../../components/_Helpers/SvgMask';

// UTILS
import { guid } from '../../../libs/utils';

// TYPES
import { AlertActiveListAlert } from '../../../api/models';

const AlertBar: FC = () => {
	const dispatch = useAppDispatch();

	const [isLoading, setIsLoading] = useState(true);
	const [isDismissing, setIsDismissing] = useState(false);

	// State to show/hide OffCanvas
	const [showAlertOffCanvas, setShowAlertOffCanvas] = useState(false);

	// State to store the alert list
	const [alertActiveList, setAlertActiveList] = useState<AlertActiveListAlert[]>([]);

	const getAlertActiveList = async () => {
		setIsLoading(true);

		const response = await apibridge.getAlertActiveList();

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setAlertActiveList(response.data.result.alerts || []);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Alerts error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
	};

	useEffect(() => {
		getAlertActiveList();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const confirmDismissAlert = async (alertId: string) => {
		setIsDismissing(true);

		if (!alertId) return;

		const response = await apibridge.postAlertDismiss({ alertId });

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				if (response.data.result?.hasRemainingActiveAlerts) {
					getAlertActiveList();

					dispatch(AlertSlice.actions.activateAlertStatus());
				} else {
					setAlertActiveList([]);

					dispatch(AlertSlice.actions.deactivateAlertStatus());
				}

				setShowAlertOffCanvas(false);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Alerts error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsDismissing(false);
	};

	const currentActiveAlert = alertActiveList[0];

	return alertActiveList.length === 0 ? null : (
		<div className={`alert-bar-wrapper ${isDismissing ? 'opacity-50' : ''}`}>
			<div className="alert-bar-border">
				<div className="alert-bar">
					<div className="text-content d-flex align-items-center gap-3">
						<div className="alert-icon flex-shrink-0">
							<SvgMask path="/svg/bell.svg" width={20} height={20} />
						</div>

						<span className="fw-bold text-dark-text flex-shrink-0">{currentActiveAlert?.title}</span>

						<span className="main-text">
							{currentActiveAlert?.description ? (
								<RichText content={{ markup: currentActiveAlert.description }} />
							) : null}
						</span>
					</div>

					<div className="link-group">
						<button
							className="btn btn-link"
							onClick={() => {
								setShowAlertOffCanvas(true);
							}}
							disabled={isDismissing}
						>
							View details
						</button>

						<button
							className="btn btn-link"
							onClick={() => {
								confirmDismissAlert(currentActiveAlert?.id || '');
							}}
							disabled={isDismissing}
						>
							Dismiss
						</button>
					</div>

					<Offcanvas
						bsPrefix="view-alert offcanvas"
						show={showAlertOffCanvas}
						onHide={() => setShowAlertOffCanvas(false)}
						placement="end"
					>
						<Offcanvas.Header closeButton>
							<Offcanvas.Title className="d-flex justify-content-start align-items-center w-100">
								<h2 className="text-shades-800 mb-0">
									<strong>{currentActiveAlert?.title}</strong>
								</h2>
							</Offcanvas.Title>
						</Offcanvas.Header>

						<Offcanvas.Body className="d-flex flex-column">
							{isLoading ? (
								<div className="d-flex align-items-center justify-content-center w-100">
									<DelayedFadeIn>
										<Spinner animation="border" role="status">
											<span className="visually-hidden">Loading...</span>
										</Spinner>
									</DelayedFadeIn>
								</div>
							) : (
								<>
									<div className="mb-0 text-dark-text flex-grow-1">
										{currentActiveAlert?.content ? <RichText content={{ markup: currentActiveAlert.content }} /> : null}
									</div>

									<button
										className="btn btn-primary btn-white"
										onClick={() => confirmDismissAlert(currentActiveAlert?.id || '')}
										disabled={isDismissing}
									>
										Dismiss
									</button>
								</>
							)}
						</Offcanvas.Body>
					</Offcanvas>
				</div>
			</div>
		</div>
	);
};

export default AlertBar;
